import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface UseOAuthOptions {
  onSuccess: (data: any) => void;
  onError: (error: any) => void;
}

interface OAuthMessage {
  type: 'oauth-success' | 'oauth-error' | 'oauth-callback';
  [key: string]: any;
}

const OAUTH_STORAGE_KEY = 'oauth_callback_result';
const POPUP_POLL_INTERVAL = 200;
const STORAGE_POLL_INTERVAL = 200;
// After popup closes, continue polling localStorage for this duration
// (window.close() often fails after cross-origin OAuth redirects)
const POST_CLOSE_GRACE_PERIOD = 2000;

export const useOAuth = ({ onSuccess, onError }: UseOAuthOptions) => {
  const { t } = useTranslation('common');
  const [isConnecting, setIsConnecting] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const popupPollingRef = useRef<NodeJS.Timeout | null>(null);
  const storagePollingRef = useRef<NodeJS.Timeout | null>(null);
  const graceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handleMessageRef = useRef<((event: MessageEvent) => void) | null>(null);
  const handleStorageRef = useRef<((event: StorageEvent) => void) | null>(null);
  const successHandledRef = useRef(false);

  // Check localStorage for OAuth result (active polling, not just events)
  const checkLocalStorage = useCallback(() => {
    try {
      const stored = localStorage.getItem(OAUTH_STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        // Clear immediately to prevent double-processing
        localStorage.removeItem(OAUTH_STORAGE_KEY);
        return data;
      }
    } catch (e) {
      console.error('Error reading OAuth result from localStorage', e);
    }
    return null;
  }, []);

  // Fully cleanup everything (listeners, timers, state)
  const fullCleanup = useCallback(() => {
    if (popupPollingRef.current) {
      clearInterval(popupPollingRef.current);
      popupPollingRef.current = null;
    }
    if (storagePollingRef.current) {
      clearInterval(storagePollingRef.current);
      storagePollingRef.current = null;
    }
    if (graceTimeoutRef.current) {
      clearTimeout(graceTimeoutRef.current);
      graceTimeoutRef.current = null;
    }
    if (handleMessageRef.current) {
      window.removeEventListener('message', handleMessageRef.current);
      handleMessageRef.current = null;
    }
    if (handleStorageRef.current) {
      window.removeEventListener('storage', handleStorageRef.current);
      handleStorageRef.current = null;
    }
    setIsConnecting(false);
    popupRef.current = null;
    successHandledRef.current = false;
  }, []);

  // Handle OAuth result (from any source)
  const handleOAuthResult = useCallback((data: any) => {
    // Prevent double-handling
    if (successHandledRef.current) return;
    successHandledRef.current = true;

    if (data.type === 'oauth-success') {
      // Stop polling but don't call fullCleanup yet to preserve popupRef and isConnecting state
      if (popupPollingRef.current) {
        clearInterval(popupPollingRef.current);
        popupPollingRef.current = null;
      }
      if (storagePollingRef.current) {
        clearInterval(storagePollingRef.current);
        storagePollingRef.current = null;
      }
      onSuccess(data);
    } else if (data.type === 'oauth-error') {
      fullCleanup();
      onError(data);
    }
  }, [fullCleanup, onSuccess, onError]);

  const initiateOAuth = useCallback((url: string) => {
    // Clean up any existing listeners/timers from previous runs
    fullCleanup();

    // Clear any stale OAuth result from previous attempts
    localStorage.removeItem(OAUTH_STORAGE_KEY);

    setIsConnecting(true);
    successHandledRef.current = false;

    // Define message handler (for when window.opener still works)
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      const data = event.data as OAuthMessage;
      if (data.type === 'oauth-success' || data.type === 'oauth-error') {
        handleOAuthResult(data);
      }
    };

    // Define storage handler (fallback for severed opener links)
    const handleStorage = (event: StorageEvent) => {
      if (event.key === OAUTH_STORAGE_KEY && event.newValue) {
        try {
          const data = JSON.parse(event.newValue);
          // Clear immediately
          localStorage.removeItem(OAUTH_STORAGE_KEY);
          handleOAuthResult(data);
        } catch (e) {
          console.error('Error parsing storage message', e);
        }
      }
    };

    // Store and add listeners
    handleMessageRef.current = handleMessage;
    handleStorageRef.current = handleStorage;

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);

    // Open popup
    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popup = window.open(
      url,
      'OAuth Authorization',
      `width=${width},height=${height},left=${left},top=${top}`
    );

    popupRef.current = popup;

    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      fullCleanup();
      onError({ message: t('channels.popupBlocked') });
      return;
    }

    // Active polling for localStorage (storage events are unreliable across tabs)
    // This is crucial for cross-origin OAuth where window.opener is severed
    storagePollingRef.current = setInterval(() => {
      const result = checkLocalStorage();
      if (result) {
        handleOAuthResult(result);
      }
    }, STORAGE_POLL_INTERVAL);

    // Poll for popup closure
    popupPollingRef.current = setInterval(() => {
      if (popup.closed) {
        // Stop popup polling
        if (popupPollingRef.current) {
          clearInterval(popupPollingRef.current);
          popupPollingRef.current = null;
        }

        // Check localStorage one more time immediately
        const result = checkLocalStorage();
        if (result) {
          handleOAuthResult(result);
          return;
        }

        // The popup closed, but window.close() often fails after cross-origin OAuth.
        // Continue polling localStorage for a grace period (popup might still be open but unreachable)
        console.log('OAuth popup appears closed, continuing localStorage polling for grace period...');

        // Set up grace period timeout
        graceTimeoutRef.current = setTimeout(() => {
          // Final check before giving up
          const finalResult = checkLocalStorage();
          if (finalResult) {
            handleOAuthResult(finalResult);
          } else if (!successHandledRef.current) {
            // Only show error if we haven't already handled success
            // The user might have manually closed the popup, or the OAuth actually failed
            console.log('OAuth flow ended without receiving result');
            fullCleanup();
          }
        }, POST_CLOSE_GRACE_PERIOD);
      }
    }, POPUP_POLL_INTERVAL);

  }, [fullCleanup, checkLocalStorage, handleOAuthResult, t, onError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      fullCleanup();
    };
  }, [fullCleanup]);

  return {
    isConnecting,
    initiateOAuth,
    closePopup: () => {
      if (popupRef.current) popupRef.current.close();
      fullCleanup();
    }
  };
};
